import { Box, Grid, Typography, Dialog, DialogTitle, DialogContent, TextField, Button, Select, MenuItem, FormControl } from "@mui/material";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMediaQuery } from "@mui/material";
import Link from "@mui/material/Link";
import { useTheme } from "@mui/material/styles";
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import { generateDeviceFingerprint } from '../utils/deviceFingerprint';

const icons = [
  { icon: './auto_suv_vehicle_car_automobile_icon.png', title: '190K+ Fahrzeugtypen', style: {width: '80px', height: 'auto'} }, 
  { icon: './disc.png', title: '9.8M+ Artikeldaten', style: {width: '70px', height: 'auto', paddingTop: '5px', paddingBottom: '5px'} },
  { icon: './brands.png', title: '1000+ Marken', style: {width: '80px', height: 'auto', paddingTop: '20px', paddingBottom: '20px'} },
  { icon: './add_user_human_person_plus_icon.png', title: 'Unbegrenzte Benutzer', style: {width: '80px', height: 'auto'} },
  { icon: './bag_buy_cart_sell_shop_icon.png', title: 'Online-Erlebnis', style: {width: '80px', height: 'auto'} },
];

interface LogoProps {
  className?: string;
  width?: number;
  height?: number;
}

const Login: React.FC<LogoProps> = ({ className, width, height }) => {
  const baseURL = process.env.REACT_APP_API_URL;
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [warning, setWarning] = useState("");
  const [loading, setLoading] = useState(false);
  const [registerOpen, setRegisterOpen] = useState(false);
  const [registerLoading, setRegisterLoading] = useState(false);
  const [registerError, setRegisterError] = useState("");
  const [registerSuccess, setRegisterSuccess] = useState("");
  const [regUsername, setRegUsername] = useState("");
  const [regName, setRegName] = useState("");
  const [regSurname, setRegSurname] = useState("");
  const [regCompany, setRegCompany] = useState("");
  const [regCity, setRegCity] = useState("");
  const [regCountry, setRegCountry] = useState("Switzerland");
  const [regEmail, setRegEmail] = useState("");
  const [regPassword, setRegPassword] = useState("");
  const [regPhone, setRegPhone] = useState("");
  const [regAddress, setRegAddress] = useState("");
  const [regUstId, setRegUstId] = useState("");
  const [regPlz, setRegPlz] = useState("");
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const navigate = useNavigate();

  const validateForm = React.useCallback(() => {
    if (username.length === 0 || password.length === 0) {
      setError("");
      setWarning("Benutzername und Passwort sind erforderlich");
      return false;
    }
    setWarning("");
    return true;
  }, [username, password]);

  const handleSubmit = async (event: any) => {
    event.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    setError("");
    setWarning("");
  
    try {
      // Generate device fingerprint
      const deviceData = await generateDeviceFingerprint();
      
      // Create form data for login
      const formDetails = new URLSearchParams();
      formDetails.append("username", username);
      formDetails.append("password", password);
      
      // Add device data as JSON string
      formDetails.append("device_data", JSON.stringify(deviceData));
  
      const response = await fetch(`${baseURL}/auth/token`, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: formDetails,
      });

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem("token", data.access_token);
        localStorage.setItem("user", JSON.stringify(data.user));

        // Redirect to previous location if available
        if (data.user.is_admin) {
          navigate("/admin/dashboard");
        } else {
          navigate("/");
        }
      } else {
        const errorData = await response.json();
        const errorMessage = errorData.detail || "Anmeldung fehlgeschlagen!";
        
        // Check if it's a device restriction error
        if (response.status === 403 && errorMessage.includes("Device not recognized")) {
          setWarning(
            "⚠️ Gerätezugriff eingeschränkt\n\n" +
            "Sie versuchen, sich von einem anderen Gerät anzumelden als dem, mit dem Sie sich registriert haben. " +
            "Aus Sicherheitsgründen können Sie sich nur von demselben Gerät anmelden, das Sie bei Ihrer ersten Anmeldung verwendet haben.\n\n" +
            "Wenn Sie von einem anderen Gerät auf Ihr Konto zugreifen müssen, wenden Sie sich bitte an Ihren Administrator."
          );
        } else {
          setError(errorMessage);
        }
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);
      setError("Ein Fehler ist aufgetreten. Bitte versuchen Sie es später erneut.");
    }
  };

  const handleRegisterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setRegisterError("");
    setRegisterSuccess("");
    setRegisterLoading(true);
    try {
      const payload = {
        username: regUsername,
        name: regName,
        surname: regSurname,
        company: regCompany,
        city: regCity || undefined,
        country: regCountry || "Switzerland",
        email: regEmail,
        password: regPassword,
        phone: regPhone || undefined,
        address: regAddress,
        zone: regCountry || "Switzerland",
        group: "A",
        ust_id: regUstId || undefined,
        plz: regPlz || undefined,
      };
      const response = await fetch(`${baseURL}/auth/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        setRegisterSuccess(data.message || "Registrierung eingereicht. Bitte warten Sie auf die Freigabe durch den Administrator.");
        setTimeout(() => {
          setRegisterOpen(false);
          setRegisterSuccess("");
          setRegUsername("");
          setRegName("");
          setRegSurname("");
          setRegCompany("");
          setRegCity("");
          setRegCountry("Switzerland");
          setRegEmail("");
          setRegPassword("");
          setRegPhone("");
          setRegAddress("");
          setRegUstId("");
          setRegPlz("");
        }, 2000);
      } else {
        const errMsg = typeof data.detail === "string"
          ? data.detail
          : Array.isArray(data.detail)
          ? data.detail.map((d: any) => d.msg || d.loc?.join(".") || JSON.stringify(d)).join(" ")
          : data.detail?.message || JSON.stringify(data.detail || "Registrierung fehlgeschlagen");
        setRegisterError(errMsg);
      }
    } catch (err) {
      setRegisterError("Es ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut.");
    } finally {
      setRegisterLoading(false);
    }
  };

  const textFieldSx = {
    "& .MuiOutlinedInput-root": {
      fontSize: "13px",
      fontFamily: "Inter",
    },
  };

  /** Visible label above each control (avoids theme issues hiding MUI InputLabel text in the notch). */
  const registerFieldLabelSx = {
    fontSize: "13px",
    fontFamily: "Inter",
    fontWeight: 600,
    color: "text.primary",
    display: "block",
    mb: 0.5,
  };

  const registerTextFieldCommon = {
    fullWidth: true,
    variant: "outlined" as const,
    size: "small" as const,
    hiddenLabel: true,
    sx: textFieldSx,
  };

  return (
    <>
    <Grid container spacing={1}>
      {!isTablet && (
        <Grid item xs={0} sm={0} md={6} lg={8} sx={{backgroundColor: theme.palette.primary.main,}}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100vh',
            }}
          >
            <Grid 
              container 
              spacing={2}
              justifyContent="center"
              alignItems="center"
            >
              {icons.map((iconData, index) => (
                <Grid key={index} item xs={4} md={2}>
                  <div className={`icon-animation icon-appear-${index + 1}`}>
                    <Box textAlign="center">
                      <img 
                        src={iconData.icon} 
                        alt={iconData.title} 
                        style={iconData.style}
                      />
                      <Typography variant="subtitle1" sx={{fontSize: "13px", fontFamily: "Inter", color: 'white'}}>
                        {iconData.title}
                      </Typography>
                    </Box>
                  </div>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Grid>
      )}

      <Grid item xs={12} sm={12} md={6} lg={4}>
        <div
          className="container"
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
          }}
        >
          <div className="login-form" style={{ width: "380px" }}>
          {/* <img
              src="./logo.png" // Path relative to the public folder
              alt="IAP Logo"
              style={{
                display: "block",
                margin: "0 auto",
                width: "100%", // Adjust as needed
              }}
            /> */}

            <div className="row" style={{marginTop: '20px'}}>
              <div className="col-md-6 offset-md-3">
                <form onSubmit={handleSubmit}>
                  <div className="form-group">
                    <input
                      required
                      type="text"
                      className="form-control"
                      id="username"
                      placeholder="Benutzername eingeben"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      style={{
                        borderRadius: "5px",
                        border: "1px solid #ccc",
                        padding: "10px",
                        margin: "5px 0px",
                        boxSizing: "border-box",
                        width: "100%",
                      }}
                    />
                  </div>
                  <div className="form-group">
                    <input
                      required
                      type="password"
                      className="form-control"
                      id="password"
                      placeholder="Passwort eingeben"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      style={{
                        borderRadius: "5px",
                        border: "1px solid #ccc",
                        padding: "10px",
                        margin: "5px 0px",
                        boxSizing: "border-box",
                        width: "100%",
                      }}
                    />
                  </div>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading}
                    style={{
                      cursor: "pointer",
                      background: theme.palette.secondary.main,
                      color: theme.palette.primary.light,
                      borderRadius: "5px",
                      border: 0,
                      boxShadow: "none",
                      padding: "10px",
                      margin: "5px 0px",
                      boxSizing: "border-box",
                      width: "100%",
                    }}
                  >
                    {loading ? "Wird geladen..." : "Anmelden"}
                  </button>
                  <Box sx={{ textAlign: "center", mt: 1 }}>
                    <Link
                      component="button"
                      type="button"
                      variant="body2"
                      onClick={() => setRegisterOpen(true)}
                      sx={{
                        fontSize: "14px",
                        fontFamily: "Inter",
                        cursor: "pointer",
                        textDecoration: "underline",
                      }}
                    >
                      Registrieren
                    </Link>
                  </Box>
                  <Stack sx={{ width: "100%", height: "48px" }} spacing={0}>
                    {error && (
                      <Alert
                        severity="error"
                        sx={{
                          fontFamily: "Inter",
                          fontWeight: 300,
                        }}
                      >
                        {error}
                      </Alert>
                    )}
                    {warning && (
                      <Alert
                        severity="warning"
                        sx={{
                          fontFamily: "Inter",
                          fontWeight: 300,
                        }}
                      >
                        {warning}
                      </Alert>
                    )}
                  </Stack>
                </form>
              </div>
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginTop: "20px",
              }}
            >
              <span
                style={{
                  marginRight: "10px",
                  fontSize: "14px",
                  fontFamily: "Inter",
                }}
              >
                Powered by
              </span>
              <Link
                    href="https://www.tecmotion.co"
                    target="_blank"
                    rel="noopener"
                    sx={{ ml: 1, color: "#fff" }}
                  >
              <img
                src="./tecm_logo.png"
                alt="Tecm Logo"
                style={{
                  width: "150px",
                }}
              />
              </Link>
            </div>
          </div>
        </div>
      </Grid>
    </Grid>

    <Dialog
      open={registerOpen}
      onClose={() => !registerLoading && setRegisterOpen(false)}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "8px",
          fontFamily: "Inter",
        },
      }}
    >
      <DialogTitle sx={{ fontFamily: "Inter", fontSize: "18px" }}>Registrieren</DialogTitle>
      <form onSubmit={handleRegisterSubmit}>
        <DialogContent dividers>
          <Grid container spacing={1.5}>
            {registerSuccess && (
              <Grid item xs={12}>
                <Alert severity="success" sx={{ fontFamily: "Inter" }}>{registerSuccess}</Alert>
              </Grid>
            )}
            {registerError && (
              <Grid item xs={12}>
                <Alert severity="error" sx={{ fontFamily: "Inter" }}>{registerError}</Alert>
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <Typography component="label" htmlFor="reg-username" sx={registerFieldLabelSx}>
                Benutzername <Box component="span" sx={{ color: "error.main" }} aria-hidden>*</Box>
              </Typography>
              <TextField
                {...registerTextFieldCommon}
                id="reg-username"
                required
                value={regUsername}
                onChange={(e) => setRegUsername(e.target.value)}
                inputProps={{ "aria-label": "Benutzername" }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography component="label" htmlFor="reg-email" sx={registerFieldLabelSx}>
                E-Mail <Box component="span" sx={{ color: "error.main" }} aria-hidden>*</Box>
              </Typography>
              <TextField
                {...registerTextFieldCommon}
                id="reg-email"
                required
                type="email"
                value={regEmail}
                onChange={(e) => setRegEmail(e.target.value)}
                inputProps={{ "aria-label": "E-Mail" }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography component="label" htmlFor="reg-name" sx={registerFieldLabelSx}>
                Name <Box component="span" sx={{ color: "error.main" }} aria-hidden>*</Box>
              </Typography>
              <TextField
                {...registerTextFieldCommon}
                id="reg-name"
                required
                value={regName}
                onChange={(e) => setRegName(e.target.value)}
                inputProps={{ "aria-label": "Name" }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography component="label" htmlFor="reg-surname" sx={registerFieldLabelSx}>
                Nachname <Box component="span" sx={{ color: "error.main" }} aria-hidden>*</Box>
              </Typography>
              <TextField
                {...registerTextFieldCommon}
                id="reg-surname"
                required
                value={regSurname}
                onChange={(e) => setRegSurname(e.target.value)}
                inputProps={{ "aria-label": "Nachname" }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography component="label" htmlFor="reg-company" sx={registerFieldLabelSx}>
                Firma <Box component="span" sx={{ color: "error.main" }} aria-hidden>*</Box>
              </Typography>
              <TextField
                {...registerTextFieldCommon}
                id="reg-company"
                required
                value={regCompany}
                onChange={(e) => setRegCompany(e.target.value)}
                inputProps={{ "aria-label": "Firma" }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography component="label" htmlFor="reg-ustid" sx={registerFieldLabelSx}>
                UST-ID
              </Typography>
              <TextField
                {...registerTextFieldCommon}
                id="reg-ustid"
                value={regUstId}
                onChange={(e) => setRegUstId(e.target.value)}
                inputProps={{ "aria-label": "UST-ID" }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography component="label" htmlFor="reg-plz" sx={registerFieldLabelSx}>
                PLZ (Postleitzahl)
              </Typography>
              <TextField
                {...registerTextFieldCommon}
                id="reg-plz"
                value={regPlz}
                onChange={(e) => setRegPlz(e.target.value)}
                inputProps={{ "aria-label": "PLZ (Postleitzahl)" }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography component="label" htmlFor="reg-city" sx={registerFieldLabelSx}>
                Ort
              </Typography>
              <TextField
                {...registerTextFieldCommon}
                id="reg-city"
                value={regCity}
                onChange={(e) => setRegCity(e.target.value)}
                inputProps={{ "aria-label": "Ort" }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography component="label" id="reg-country-label" htmlFor="reg-country" sx={registerFieldLabelSx}>
                Land <Box component="span" sx={{ color: "error.main" }} aria-hidden>*</Box>
              </Typography>
              <FormControl fullWidth size="small" variant="outlined" sx={textFieldSx}>
                <Select
                  id="reg-country"
                  labelId="reg-country-label"
                  value={regCountry}
                  onChange={(e) => setRegCountry(e.target.value)}
                  inputProps={{ "aria-label": "Land" }}
                >
                  <MenuItem value="Deutschland">Deutschland</MenuItem>
                  <MenuItem value="Switzerland">Switzerland</MenuItem>
                  <MenuItem value="Liechtenstein">Liechtenstein</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Typography component="label" htmlFor="reg-address" sx={registerFieldLabelSx}>
                Adresse <Box component="span" sx={{ color: "error.main" }} aria-hidden>*</Box>
              </Typography>
              <TextField
                {...registerTextFieldCommon}
                id="reg-address"
                required
                value={regAddress}
                onChange={(e) => setRegAddress(e.target.value)}
                inputProps={{ "aria-label": "Adresse" }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography component="label" htmlFor="reg-phone" sx={registerFieldLabelSx}>
                Telefon
              </Typography>
              <TextField
                {...registerTextFieldCommon}
                id="reg-phone"
                value={regPhone}
                onChange={(e) => setRegPhone(e.target.value)}
                inputProps={{ "aria-label": "Telefon" }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography component="label" htmlFor="reg-password" sx={registerFieldLabelSx}>
                Passwort <Box component="span" sx={{ color: "error.main" }} aria-hidden>*</Box>
              </Typography>
              <TextField
                {...registerTextFieldCommon}
                id="reg-password"
                required
                type="password"
                value={regPassword}
                onChange={(e) => setRegPassword(e.target.value)}
                inputProps={{ "aria-label": "Passwort" }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <Box sx={{ p: 2, display: "flex", justifyContent: "flex-end", gap: 1 }}>
          <Button
            onClick={() => !registerLoading && setRegisterOpen(false)}
            disabled={registerLoading}
            sx={{ fontFamily: "Inter" }}
          >
            Abbrechen
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={registerLoading}
            sx={{
              fontFamily: "Inter",
              bgcolor: theme.palette.secondary.main,
              "&:hover": { bgcolor: theme.palette.secondary.dark },
            }}
          >
            {registerLoading ? "Wird gesendet..." : "Registrieren"}
          </Button>
        </Box>
      </form>
    </Dialog>
    </>
  );
};

export default Login;
